import { create } from "zustand";
import type {
  CartResponse,
  CustomerInfo,
} from "../types";
import { setSessionCookie } from "../lib/session-cookie";

/**
 * État du widget panier standalone
 */

type CartStep = "cart" | "info" | "payment" | "success";

interface CartStoreState {
  // Données du panier
  cart: CartResponse["cart"] | null;
  isLoading: boolean;
  error: string | null;

  // Étape courante
  currentStep: CartStep;

  // Informations client
  customerInfo: Partial<CustomerInfo>;

  // État paiement
  clientSecret: string | null;
  stripePublishableKey: string | null;
  cartNumber: string | null;
  bookingIds: string[] | null;
  isSubmitting: boolean;
  isRequestBooking: boolean;

  // Actions
  setCart: (cart: CartResponse["cart"]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setCurrentStep: (step: CartStep) => void;
  setCustomerInfo: (info: Partial<CustomerInfo>) => void;
  setClientSecret: (secret: string | null) => void;
  setStripePublishableKey: (key: string | null) => void;
  setCartNumber: (number: string | null) => void;
  setBookingIds: (ids: string[] | null) => void;
  setSubmitting: (submitting: boolean) => void;
  setIsRequestBooking: (isRequest: boolean) => void;
  removeItem: (bookingId: string) => void;
  resetCart: () => void;
}

export const useCartStore = create<CartStoreState>((set) => ({
  cart: null,
  isLoading: true,
  error: null,

  currentStep: "cart",

  customerInfo: {},

  clientSecret: null,
  stripePublishableKey: null,
  cartNumber: null,
  bookingIds: null,
  isSubmitting: false,
  isRequestBooking: false,

  setCart: (cart) => set({ cart, isLoading: false, error: null }),
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error, isLoading: false }),
  setCurrentStep: (step) => set({ currentStep: step }),
  setCustomerInfo: (info) =>
    set((state) => ({
      customerInfo: { ...state.customerInfo, ...info },
    })),
  setClientSecret: (secret) => set({ clientSecret: secret }),
  setStripePublishableKey: (key) => set({ stripePublishableKey: key }),
  setCartNumber: (number) => set({ cartNumber: number }),
  setBookingIds: (ids) => set({ bookingIds: ids }),
  setSubmitting: (submitting) => set({ isSubmitting: submitting }),
  setIsRequestBooking: (isRequest) => set({ isRequestBooking: isRequest }),
  removeItem: (bookingId) =>
    set((state) => {
      if (!state.cart) return {};
      const removed = state.cart.bookings.find((b) => b.id === bookingId);
      const newBookings = state.cart.bookings.filter(
        (b) => b.id !== bookingId,
      );
      return {
        cart: {
          ...state.cart,
          bookings: newBookings,
          totalAmount:
            state.cart.totalAmount - (removed?.itemsTotal || 0),
          itemsCount: newBookings.length,
        },
      };
    }),
  resetCart: () => {
    // Supprimer la session du localStorage
    setSessionCookie(null);
    // Réinitialiser tout l'état du store
    set({
      cart: null,
      isLoading: false,
      error: null,
      currentStep: "cart",
      customerInfo: {},
      clientSecret: null,
      stripePublishableKey: null,
      cartNumber: null,
      bookingIds: null,
      isSubmitting: false,
      isRequestBooking: false,
    });
  },
}));
